import type { ExpenseCategory, ExpenseScope } from "../../lib/api/expenses";

export const EXPENSE_SCOPE_ALL_STORES: ExpenseScope = "all_stores";
export const EXPENSE_SCOPE_STORE: ExpenseScope = "store";
export const EXPENSE_SCOPE_ONLINE: ExpenseScope = "online";
export const EXPENSE_SCOPE_LOGISTICS: ExpenseScope = "logistics";

export const EXPENSE_SCOPE_LABELS: Record<ExpenseScope, string> = {
    all_stores: "Всички магазини",
    store: "Магазин",
    online: "Онлайн",
    logistics: "Логистика",
};

export type ExpenseAreaId = "transport" | "marketing" | "salaries";

export type ExpenseTransportSubgroupId = "fuel" | "depreciation" | "leasing";

export const EXPENSE_AREA_META: Record<
    ExpenseAreaId,
    { label: string; title: string; description: string }
> = {
    transport: {
        label: "Транспорт",
        title: "Отчет за автомобилни разходи",
        description: "Гориво, амортизация и лизинг — бусове и администрация.",
    },
    marketing: {
        label: "Маркетинг",
        title: "Маркетинг",
        description: "Радио, листовки и банери по месец.",
    },
    salaries: {
        label: "Заплати",
        title: "Заплати",
        description: "Заплати по екипи — логистика, маркетинг, администрация и шофьори.",
    },
};

export const EXPENSE_AREA_TREE: Record<
    Exclude<ExpenseAreaId, "transport">,
    { title: string; summaryTitle: string; grandTotalLabel: string }
> = {
    marketing: {
        title: "Разходи за маркетинг",
        summaryTitle: "Информяция за всички маркетингови разходи за избраният период",
        grandTotalLabel: "Общо маркетингови разходи",
    },
    salaries: {
        title: "Заплати",
        summaryTitle: "Информация за всички разходи за заплати за избраният период",
        grandTotalLabel: "Общо разходи за заплати",
    },
};

export const EXPENSE_TRANSPORT_SUBGROUPS = [
    {
        id: "fuel" as const,
        label: "Гориво",
        description: "Гориво за бусове и администрация.",
    },
    {
        id: "depreciation" as const,
        label: "Амортизация",
        description: "Амортизация на бусове и администрация.",
    },
    {
        id: "leasing" as const,
        label: "Лизинги",
        description: "Лизингови вноски за бусове и администрация.",
    },
];

export const EXPENSE_MONTHS = [
    { month: 1, label: "Януари" },
    { month: 2, label: "Февруари" },
    { month: 3, label: "Март" },
    { month: 4, label: "Април" },
    { month: 5, label: "Май" },
    { month: 6, label: "Юни" },
    { month: 7, label: "Юли" },
    { month: 8, label: "Август" },
    { month: 9, label: "Септември" },
    { month: 10, label: "Октомври" },
    { month: 11, label: "Ноември" },
    { month: 12, label: "Декември" },
] as const;

export function categoriesForArea(
    areaId: ExpenseAreaId,
    options: Array<{ id: ExpenseCategory; label: string; area: string; subgroup: string | null }>,
) {
    return options.filter((option) => option.area === areaId);
}

export function categoriesForTransportSubgroup(
    subgroupId: ExpenseTransportSubgroupId,
    options: Array<{ id: ExpenseCategory; label: string; area: string; subgroup: string | null }>,
) {
    return options.filter((option) => option.area === "transport" && option.subgroup === subgroupId);
}

export function entryKey(
    scope: ExpenseScope,
    storeName: string | null,
    category: ExpenseCategory,
    year: number,
    month: number,
) {
    return `${scope}:${storeName ?? ""}:${category}:${year}:${month}`;
}

export function formatFuelDateTime(value: string | null | undefined) {
    if (!value) {
        return "—";
    }

    const parsed = new Date(value.includes("T") ? value : value.replace(" ", "T"));
    if (Number.isNaN(parsed.getTime())) {
        return value;
    }

    return parsed.toLocaleString("bg-BG", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
}

export function formatAmount(value: number | null | undefined) {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return "—";
    }

    return Number(value).toLocaleString("bg-BG", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
}

export function currentYear() {
    return new Date().getFullYear();
}

export function currentMonth() {
    return new Date().getMonth() + 1;
}

export function scopeFilterLabel(scope: ExpenseScope, storeName: string | null) {
    if (scope === EXPENSE_SCOPE_STORE && storeName) {
        return storeName;
    }

    return EXPENSE_SCOPE_LABELS[scope];
}
